import { useEffect, useState } from "react";
import { SpaceBetween, Button, Textarea } from "@amzn/awsui-components-react";
import { CSRFService } from "services/csrf-service";
import { GetCommentsCommandOutput, Order } from "@amzn/ito-client";
import { usePostComment } from "hooks";

import { useFlashBarItemsActions } from "common/UseFlashBarItems/useFlashBarItems";
import { getString } from "common/ui-string-labels/ui-strings-utils";
import { debounce } from "lodash";
import { MessageList } from "./Messages/MessagesList";
import { useOrderEditActions, useOrderEditItems } from "views/OrderDetailsSingleViewPage/useOrderEdit";
import Checkbox from '@amzn/awsui-components-react/polaris/checkbox';
import './OrderDetailsContentTabs.scss';

const stringPath = "orderDetails.tabs.messages";
const HALF_SECOND_IN_MS = 500;

interface MessagesProps {
  order: Order;
  useOrderEditItems: useOrderEditItems;
  useOrderEditActions: useOrderEditActions;
  flashBarItemsActions: useFlashBarItemsActions;
  comments?: GetCommentsCommandOutput;
  isLoadingComments?: boolean;
  inputComment: string;
  setInputComment: (value: string) => void;
  commentsSource: string;
  isAmznham?: boolean;
  isItseOrder?: boolean;
  canComment?: boolean;
  onRefreshComments: () => void;
}

export const MessagesTab = (props: MessagesProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { doPostComment, error: errorPostComment, isLoading: isLoadingPost, response } = usePostComment(props.isAmznham);
  const [responseRequiredChecked, setResponseRequiredChecked] = useState(false);

  useEffect(() => {
    if (response && !errorPostComment) {
      props.useOrderEditActions.flagOrderWithFirstEdit();
      props.onRefreshComments();
    }
  }, [response, errorPostComment]);

  useEffect(() => {
    setIsLoading((props.isLoadingComments ?? false) || isLoadingPost);
  }, [props.isLoadingComments, isLoadingPost]);

  const debouncedSendComment = debounce(sendComment, HALF_SECOND_IN_MS);
  function handleSendComment() {
    setIsLoading(true);
    props.setInputComment("");
    debouncedSendComment();
  }

  async function sendComment() {
    const csrfService = new CSRFService();
    const csrfToken = await csrfService.getToken();
    let tags: string[] = [];
    if (props.isAmznham || props.isItseOrder) {
      tags = ["hw-order-support"]
    }
    doPostComment(props.commentsSource, props.inputComment, csrfToken, tags, responseRequiredChecked ? responseRequiredChecked : undefined);
    setResponseRequiredChecked(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {(props.canComment ?? !props.useOrderEditItems.isReadOnly) && (
        <div className="sticky-input-section">
          <SpaceBetween size="s">
            {props.isAmznham &&
              <Checkbox data-testid="response-required-check" onChange={({ detail }) => setResponseRequiredChecked(detail.checked)} name={"responseRequiredCheck"} checked={responseRequiredChecked}>
                {getString(`${stringPath}.responseRequired`)}
              </Checkbox>
            }
            <Textarea
              onChange={(event) => props.setInputComment(event.detail.value)}
              rows={5}
              value={props.inputComment}
              disabled={isLoading}
              data-testid="order-comment-input"
            />
            <Button
              variant="primary"
              onClick={handleSendComment}
              loading={isLoading}
              disabled={isLoading || !props.inputComment}
              data-testid="order-send-comment"
            >
              {getString(`${stringPath}.sendButton`)}
            </Button>
          </SpaceBetween>
        </div>
      )}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <MessageList comments={props.comments} />
      </div>
    </div>
  );
};
